import { defineStore } from 'pinia'
import LOCAL_STORAGE_KEYS from '@constants/localStorageKeys'
import { useSpace } from '@/store/modules/App/space'
import type { Component, Space } from '@/types'
import type { BlockContent } from '@/types/content'
import arrayUtils from '@utils/arrayUtils'
import { isEmpty } from '@/utils/checkers'

// simple clipboard
let clipboard: string[] = []
const clipboardHistoryKey = 'clipboard-history'
const simpleClipboardKey = 'clipboard'

if (localStorage.getItem(simpleClipboardKey)) {
  try {
    const item = localStorage.getItem(simpleClipboardKey)
    clipboard = item ? JSON.parse(item) : []
  } catch (_e) {
    clipboard = []
  }
}

// block library clipboard
let blockLibraryClipboard = [] as Component[]
let blockLibraryClipboardSpaceId = null

const blockClipboardFromStorage = localStorage.getItem(
  LOCAL_STORAGE_KEYS.block_library_clipboard,
)

if (blockClipboardFromStorage) {
  try {
    blockLibraryClipboard = JSON.parse(blockClipboardFromStorage)
    blockLibraryClipboardSpaceId = localStorage.getItem(
      LOCAL_STORAGE_KEYS.block_library_clipboard_space_id,
    )
  } catch (_e) {
    blockLibraryClipboard = []
    blockLibraryClipboardSpaceId = null
  }
}

const syncWithStorage = (key: string, data: unknown): void => {
  localStorage.setItem(key, JSON.stringify(data))
}

const getCorrectSpaceToHistory = (spaceData: {
  parent_id?: Space['parent_id']
  id: Space['id']
}): number => {
  return spaceData.parent_id || spaceData.id
}

const getClipboardHistoryFromSpace = (
  stateData: State,
): { data?: unknown[]; default?: unknown[] } => {
  const { activeSpaceId } = stateData.clipboardConfig
  if (activeSpaceId === null) {
    return {}
  }
  return arrayUtils.clone(stateData.clipboardHistory[activeSpaceId] || {})
}

const getClipboardHistoryData = (stateData: State): unknown[] => {
  return getClipboardHistoryFromSpace(stateData)?.data || []
}

const getDefaultClipboardHistory = (stateData: State): unknown[] => {
  const defaultData = getClipboardHistoryFromSpace(stateData)?.default || []

  return defaultData?.constructor === Object ? [defaultData] : defaultData
}

type ClipBoardConfig = {
  useHistoryApp: boolean
  activeSpaceId: number | null
}

export type State = {
  blockClipboard: unknown[]
  selectedBlocks: BlockContent[]
  clipboardHistory: Record<string, unknown>
  clipboardConfig: ClipBoardConfig
  blockLibraryClipboard: Component[]
  blockLibraryClipboardSpaceId: number | null | string
}

const state = (): State => ({
  blockClipboard: clipboard,
  selectedBlocks: [],
  clipboardHistory: {},
  clipboardConfig: {
    useHistoryApp: false,
    activeSpaceId: null,
  },
  blockLibraryClipboard,
  blockLibraryClipboardSpaceId,
})

export const useClipboard = defineStore('clipboard', {
  state,
  getters: {
    getBlockInClipboard(state: State): unknown[] {
      if (this.useHistoryApp) {
        return getClipboardHistoryData(this)
      }

      return state.blockClipboard || []
    },

    hasBlockInClipboard(): boolean {
      return Object.keys(this.getBlockInClipboard).length > 0
    },

    blockToPasteFromClipboardHistory(): unknown[] {
      if (this.useHistoryApp) {
        const defaultClipboardData = getDefaultClipboardHistory(this)
        const hasFirstBlock = !isEmpty(this.getBlockInClipboard[0])
        const data = hasFirstBlock ? [this.getBlockInClipboard[0]] : []
        return isEmpty(defaultClipboardData) ? data : defaultClipboardData
      }

      return this.getBlockInClipboard
    },

    getSelectedBlocks: (state: State): BlockContent[] => state.selectedBlocks,
    selectedBlocksLength: (state: State): number => state.selectedBlocks.length,
    useHistoryApp: (state: State): boolean =>
      state.clipboardConfig.useHistoryApp || false,
    getBlockLibraryClipboard: (state: State): Component[] =>
      state.blockLibraryClipboard,
    getBlockLibraryClipboardAmount: (state: State): number =>
      state.blockLibraryClipboard.length,
    getBlockLibraryClipboardSpaceId: (state: State): string | number | null =>
      state.blockLibraryClipboardSpaceId,
    hasBlockLibraryClipboard: (state: State): boolean =>
      state.blockLibraryClipboard.length > 0,
  },
  actions: {
    loadBlockClipboard(): void {
      if (localStorage.getItem(simpleClipboardKey)) {
        try {
          const item = localStorage.getItem(simpleClipboardKey)
          this.blockClipboard = item ? JSON.parse(item) : []
        } catch (_e) {
          this.blockClipboard = []
        }
        return
      }
      this.blockClipboard = []
    },

    setBlockClipboard(block: BlockContent[]): void {
      this.blockClipboard = block
      syncWithStorage(simpleClipboardKey, block)
    },

    clearBlockInClipboard(): void {
      const { useHistoryApp } = this.clipboardConfig

      if (useHistoryApp) {
        return
      }

      this.blockClipboard = []
      localStorage.removeItem(simpleClipboardKey)
    },

    selectBlock(block: BlockContent): void {
      this.selectedBlocks.push(block)
    },

    deselectBlock(block: BlockContent): void {
      this.selectedBlocks = this.selectedBlocks.filter(
        (elem) => elem.block_uid !== block.block_uid,
      )
    },

    clearSelectedBlocks(): void {
      this.selectedBlocks = []
    },

    loadClipboardHistory(): void {
      const item = localStorage.getItem(clipboardHistoryKey)
      const data = item ? JSON.parse(item) : null
      this.clipboardHistory = data || {}
    },

    setClipboardConfig(config: ClipBoardConfig): void {
      this.clipboardConfig = config
    },

    clearClipboardHistoryLocal(): void {
      const { activeSpaceId } = this.clipboardConfig
      const clone = arrayUtils.clone(this.clipboardHistory || {})

      if (activeSpaceId !== null) {
        delete clone[activeSpaceId]
      }

      this.clipboardHistory = clone
      syncWithStorage(clipboardHistoryKey, clone)
    },

    setClipboardHistory(newData: Record<string, unknown>): void {
      this.clipboardHistory = newData
      syncWithStorage(clipboardHistoryKey, newData)
    },

    // copying block between spaces (block library)
    loadBlockLibraryClipboard(): void {
      const blockClipboardFromStorage = localStorage.getItem(
        LOCAL_STORAGE_KEYS.block_library_clipboard,
      )

      if (blockClipboardFromStorage) {
        try {
          this.blockLibraryClipboard = JSON.parse(blockClipboardFromStorage)
          this.blockLibraryClipboardSpaceId = localStorage.getItem(
            LOCAL_STORAGE_KEYS.block_library_clipboard_space_id,
          )
        } catch (_e) {
          this.blockLibraryClipboard = []
          this.blockLibraryClipboardSpaceId = null
        }
        return
      }
      this.blockLibraryClipboard = []
      this.blockLibraryClipboardSpaceId = null
    },

    setBlockLibraryClipboard(blockClipboard: Component[]): void {
      this.blockLibraryClipboard = blockClipboard
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.block_library_clipboard,
        JSON.stringify(blockClipboard),
      )
    },

    clearBlockLibraryClipboard(): void {
      this.blockLibraryClipboard = []
      localStorage.removeItem(LOCAL_STORAGE_KEYS.block_library_clipboard)
    },

    setBlockLibraryClipboardSpaceId(spaceId: string): void {
      this.blockLibraryClipboardSpaceId = spaceId
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.block_library_clipboard_space_id,
        spaceId,
      )
    },

    clearBlockLibraryClipboardSpaceId(): void {
      this.blockLibraryClipboardSpaceId = null
      localStorage.removeItem(
        LOCAL_STORAGE_KEYS.block_library_clipboard_space_id,
      )
    },
    initializeClipboard(config = { useHistoryApp: false }): void {
      const spaceStore = useSpace()
      this.setClipboardConfig({
        ...config,
        activeSpaceId: getCorrectSpaceToHistory(spaceStore.space),
      })

      if (config.useHistoryApp) {
        this.loadClipboardHistory()
        return
      }

      this.loadBlockClipboard()
    },

    clearClipboardHistory(): void {
      this.clearClipboardHistoryLocal()
    },

    removeLastBlockFromClipboardHistory(): void {
      const { activeSpaceId } = this.clipboardConfig
      const clone = arrayUtils.clone(this.clipboardHistory)
      const spaceHistoryData = getClipboardHistoryData(this)

      if (spaceHistoryData.length === 0) {
        return
      }
      if (spaceHistoryData.length === 1) {
        this.clearClipboardHistory()
        return
      }

      const defaultClipboardHistory = getDefaultClipboardHistory(this)
      // @ts-expect-error this has to be refactored, not touching until then
      clone[activeSpaceId].data = spaceHistoryData
      // @ts-expect-error this has to be refactored, not touching until then
      const data = clone[activeSpaceId].data
      const removedElement = data[0]

      // @ts-expect-error this has to be refactored, not touching until then
      if (defaultClipboardHistory[0]?._uid === removedElement?._uid) {
        // @ts-expect-error this has to be refactored, not touching until then
        clone[activeSpaceId].default = []
      }

      this.setClipboardHistory(clone)
    },

    removeBlockFromClipboardHistory(uuid: string): void {
      const { activeSpaceId } = this.clipboardConfig
      const clone = arrayUtils.clone(this.clipboardHistory)
      const spaceHistoryData = getClipboardHistoryData(this)

      if (spaceHistoryData.length === 0) {
        return
      }

      // @ts-expect-error this has to be refactored, not touching until then
      clone[activeSpaceId].data = spaceHistoryData.filter((block) => {
        // @ts-expect-error this has to be refactored, not touching until then
        return block._uid !== uuid
      })

      const defaultClipboardHistory = getDefaultClipboardHistory(this)
      // @ts-expect-error this has to be refactored, not touching until then
      const blocksUids = defaultClipboardHistory.map((block) => block?._uid)
      if (blocksUids.includes(uuid)) {
        // @ts-expect-error this has to be refactored, not touching until then
        clone[activeSpaceId].default = defaultClipboardHistory.filter(
          (block) => {
            // @ts-expect-error this has to be refactored, not touching until then
            return block === block?._uid
          },
        )
      }

      this.setClipboardHistory(clone)
    },

    setBlockInClipboard(block: BlockContent[]): void {
      const { useHistoryApp, activeSpaceId } = this.clipboardConfig

      if (!useHistoryApp) {
        this.setBlockClipboard(block)
        return
      }

      const clone = arrayUtils.clone(this.clipboardHistory)
      const previousData = getClipboardHistoryData(this)
      const alreadyHas20Items = previousData.length >= 20
      const newData = [...block, ...previousData]
      // @ts-expect-error this has to be refactored, not touching until then
      clone[activeSpaceId] = clone[activeSpaceId] || {}
      // @ts-expect-error this has to be refactored, not touching until then
      clone[activeSpaceId].data = newData
      // @ts-expect-error this has to be refactored, not touching until then
      clone[activeSpaceId].default = [...block]

      if (alreadyHas20Items) {
        // @ts-expect-error this has to be refactored, not touching until then
        clone[activeSpaceId].data = newData.slice(0, newData.length - 1)
      }

      this.setClipboardHistory(clone)
    },

    setDefaultBlockToClipboard(uuid: string): void {
      const { activeSpaceId } = this.clipboardConfig
      const clipboardHistoryClone = arrayUtils.clone(this.clipboardHistory)
      const spaceHistoryData = getClipboardHistoryData(this)
      // @ts-expect-error this has to be refactored, not touching until then
      clipboardHistoryClone[activeSpaceId].default = [
        spaceHistoryData.find((block): boolean => {
          // @ts-expect-error this has to be refactored, not touching until then
          return block._uid === uuid
        }),
      ]

      this.setClipboardHistory(clipboardHistoryClone)
    },
  },
})
